import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import ContactSection from "@/components/ContactSection";
import { optimizedImage } from "@/lib/image";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/7htetnuv_photo%20boutique%20abd%20.jpeg";

const beforeAfterPairs = [
  {
    id: 2,
    before: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/a2h6m1ff_avant%20.jpeg",
    after: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/6wxpprce_apre%CC%80e%CC%80s%20.jpeg",
    location: "Combles - Agon-Coutainville",
  },
  {
    id: 3,
    before: "https://customer-assets.emergentagent.com/job_a866da1c-f51c-4d49-aa5f-72bf232d531c/artifacts/yovh7w15_photo%20bd%20avnat%20.jpeg",
    after: "https://customer-assets.emergentagent.com/job_a866da1c-f51c-4d49-aa5f-72bf232d531c/artifacts/ljprwj2b_photo%20apre%CC%80s%20.jpeg",
    location: "Salon - Saint-Lô",
  },
  {
    id: 4,
    before: "https://customer-assets.emergentagent.com/job_a866da1c-f51c-4d49-aa5f-72bf232d531c/artifacts/8rd94gx3_apre%CC%80s%20.jpeg",
    after: "https://customer-assets.emergentagent.com/job_a866da1c-f51c-4d49-aa5f-72bf232d531c/artifacts/1gbfk945_avant%20.jpeg",
    location: "Entrée - Coutances",
  },
  {
    id: 1,
    before: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/qthu8bo9_AVANT%20.jpeg",
    after: "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/sle5vgwi_APR7S%20.jpeg",
    location: "Terrain extérieur - Manche",
  },
];

const services = [
  {
    id: "debarras-complet",
    title: "Débarras complet",
    description: "Successions, ventes, nous vous proposons de vous accompagner dans le débarras intégral de votre bien.",
    options: [
      {
        name: "Débarras indemnisé",
        description: "La valeur du contenu de votre bien est supérieure au coût du service, nous vous proposons une offre de rachat."
      },
      {
        name: "Débarras gratuit",
        description: "Les valeurs récupérables couvrent le coût de notre prestation, le débarras ne vous coûte rien."
      },
      {
        name: "Débarras facturé",
        description: "Le coût de notre prestation est plus important que les valeurs récupérables, nous vous facturons notre intervention."
      }
    ],
    note: "Dans les 3 options, la maison sera balayée et/ou aspirée à la fin de chaque prestation."
  },
  {
    id: "demenagement",
    title: "Déménagement",
    description: "Nous vous accompagnons dans votre déménagement à travers la France.",
    options: null,
    note: null
  },
  {
    id: "espaces-verts",
    title: "Espaces verts",
    description: "Nous vous proposons un rafraîchissement de votre jardin afin d'optimiser la vente du bien.",
    options: null,
    note: null
  },
  {
    id: "rse",
    title: "Engagement RSE",
    description: "L'ensemble du mobilier et des objets est revalorisé, recyclé, traité en déchèterie, vendu dans notre boutique à Agon-Coutainville ou donné à des personnes dans le besoin.",
    options: null,
    note: null
  }
];

const PhoneButton = ({ className, children }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className={className}>{children}</button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-white">
      <DropdownMenuItem asChild>
        <a href="tel:0608691129" className="flex items-center gap-2 cursor-pointer">
          <Phone className="w-4 h-4" /> Appeler
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <a href="sms:0608691129" className="flex items-center gap-2 cursor-pointer">
          <MessageCircle className="w-4 h-4" /> Envoyer un SMS
        </a>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const VideMaisonPage = () => {
  const navigate = useNavigate();
  const [activeGallery, setActiveGallery] = useState(0);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Floating overlay : Retour + Téléphone (sans barre blanche) */}
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          <button
            onClick={() => navigate("/")}
            className="pointer-events-auto flex items-center gap-2 text-white bg-[#4A3728]/70 hover:bg-[#4A3728] backdrop-blur-sm px-4 py-2 rounded-full transition-colors"
            data-testid="btn-retour"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour</span>
          </button>

          <PhoneButton className="pointer-events-auto flex items-center gap-2 text-white bg-[#4A3728]/70 hover:bg-[#4A3728] backdrop-blur-sm px-4 py-2 rounded-full font-medium transition-colors">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">06 08 69 11 29</span>
          </PhoneButton>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={optimizedImage(HERO_IMAGE, { w: 1920, q: 75 })} alt="Boutique Au Bon Débarras" className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#4A3728]/55 via-[#4A3728]/45 to-[#4A3728]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="font-serif text-5xl md:text-7xl text-white leading-tight mb-6">
              Au Bon Débarras
              <br />
              <span className="text-[#D4A373]">On est là pour vous&nbsp;!</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl">
              Débarras complet, enlèvement d'encombrants, recyclage des déchets, revalorisation des meubles et objets, déménagement&nbsp;!
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <Button
                onClick={() => scrollToSection("services")}
                className="bg-[#D97706] hover:bg-[#b96305] text-white rounded-full px-8 py-6 text-lg"
              >
                Découvrir nos Services
              </Button>
            </div>

            <div className="flex flex-wrap gap-8">
              <PhoneButton className="flex items-center gap-3 text-white hover:text-[#D4A373] transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="text-sm text-white/70">Appelez-nous</div>
                  <div className="font-medium">06 08 69 11 29</div>
                </div>
              </PhoneButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-[#D97706] mb-4 block">
              Nos Services
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A3728]">
              Ce que l'on vous propose
            </h2>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-sm border border-stone-200"
                data-testid={`service-${service.id}`}
              >
                <h3 className="font-serif text-2xl text-[#4A3728] mb-3">{service.title}</h3>
                <p className="text-stone-600 leading-relaxed mb-4">{service.description}</p>

                {service.options && (
                  <div className="space-y-3 mt-6">
                    {service.options.map((option) => (
                      <div key={option.name} className="bg-[#D4A373]/10 rounded-lg p-4 border-l-4 border-[#D4A373]">
                        <h4 className="font-semibold text-[#4A3728] mb-1">{option.name}</h4>
                        <p className="text-sm text-stone-600">{option.description}</p>
                      </div>
                    ))}
                  </div>
                )}

                {service.note && (
                  <div className="mt-4 p-4 bg-stone-50 rounded-lg">
                    <p className="text-sm text-stone-500 italic">{service.note}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Réalisations */}
      <section id="realisations" className="py-24 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-[#D97706] mb-4 block">
              Nos Réalisations
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A3728] mb-4">
              Avant / Après
            </h2>
          </div>

          {/* Main Gallery */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="relative rounded-lg overflow-hidden h-[400px]">
              <img
                src={optimizedImage(beforeAfterPairs[activeGallery].before, { w: 1000, q: 78 })}
                alt="Avant"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <span className="absolute bottom-4 left-4 bg-[#4A3728] text-white px-4 py-2 rounded-full text-sm font-medium">
                Avant
              </span>
              <span className="absolute top-4 left-4 bg-white/90 text-[#4A3728] px-3 py-1 rounded-full text-sm">
                {beforeAfterPairs[activeGallery].location}
              </span>
            </div>
            <div className="relative rounded-lg overflow-hidden h-[400px]">
              <img
                src={optimizedImage(beforeAfterPairs[activeGallery].after, { w: 1000, q: 78 })}
                alt="Après"
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <span className="absolute bottom-4 right-4 bg-[#D97706] text-white px-4 py-2 rounded-full text-sm font-medium">
                Après
              </span>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="flex justify-center gap-4">
            {beforeAfterPairs.map((pair, index) => (
              <button
                key={pair.id}
                onClick={() => setActiveGallery(index)}
                className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  index === activeGallery ? "border-[#D97706] scale-105" : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                <img src={optimizedImage(pair.after, { w: 160, q: 70 })} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />
    </div>
  );
};

export default VideMaisonPage;
